import { useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  Check,
  ShieldCheck,
  Repeat,
  Filter,
  Box,
  Code2,
} from "lucide-react";

import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { WEBHOOK_DOCS } from "@/lib/content";

const SECTION_NAV: ReadonlyArray<{ id: string; label: string }> = [
  { id: "envelope", label: "The envelope" },
  { id: "events", label: "Event reference" },
  { id: "filters", label: "Filters" },
  { id: "signing", label: "Signing" },
  { id: "verifiers", label: "Verifier snippets" },
  { id: "retry", label: "Retry policy" },
];

function CodeBlock({
  code,
  language,
  testId,
}: {
  code: string;
  language?: string;
  testId?: string;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div
      className="relative rounded-xl border border-mist bg-slate-900 dark:bg-navy/80 overflow-hidden group"
      data-testid={testId}
    >
      {language && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <span>{language}</span>
        </div>
      )}
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-semibold text-slate-200 hover:bg-white/10 transition-colors"
        data-testid={testId ? `${testId}-copy` : undefined}
      >
        {copied ? (
          <>
            <Check className="h-3 w-3" /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" /> Copy
          </>
        )}
      </button>
      <pre className="px-4 py-4 overflow-x-auto text-[12.5px] leading-relaxed text-slate-100 font-mono whitespace-pre">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function WebhooksDocsPage() {
  const docs = WEBHOOK_DOCS;
  return (
    <Layout>
      <SEOHead title={docs.seo.title} description={docs.seo.description} />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <Link
              href="/resources/docs"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground mb-6 transition-colors"
              data-testid="webhooks-docs-back-to-docs"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to documentation
            </Link>
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              {docs.hero.eyebrow}
            </div>
            <h1 className="text-display mb-5 text-navy dark:text-white">
              {docs.hero.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              {docs.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-6xl mx-auto">
            {/* Sticky on-page nav */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">
                  On this page
                </div>
                <nav>
                  <ul className="space-y-2">
                    {SECTION_NAV.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="block text-sm text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-16">
              {/* Envelope */}
              <section
                id="envelope"
                className="scroll-mt-24"
                data-testid="section-envelope"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Box className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {docs.envelope.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {docs.envelope.body}
                </p>
                <CodeBlock
                  code={docs.envelope.code}
                  language="HTTP"
                  testId="webhooks-docs-envelope-code"
                />
                <ul className="mt-6 space-y-2.5">
                  {docs.envelope.notes.map((note) => (
                    <li
                      key={note}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
                    >
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0" />
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Events */}
              <section
                id="events"
                className="scroll-mt-24"
                data-testid="section-events"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan flex items-center justify-center">
                    <Code2 className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    Event reference
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  All current events ship with{" "}
                  <code className="text-[12.5px] font-mono bg-slate-100 dark:bg-navy/40 px-1.5 py-0.5 rounded">
                    v: 1
                  </code>
                  . Each card shows the trigger and a representative{" "}
                  <code className="text-[12.5px] font-mono bg-slate-100 dark:bg-navy/40 px-1.5 py-0.5 rounded">
                    data
                  </code>{" "}
                  payload — the envelope wrapping is identical for every event.
                </p>
                <div className="space-y-6">
                  {docs.events.map((evt) => (
                    <div
                      key={evt.name}
                      className="rounded-2xl border border-mist bg-white dark:bg-card p-6"
                      data-testid={`webhooks-event-${evt.name}`}
                    >
                      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
                        <code className="text-sm font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                          {evt.name}
                        </code>
                        <span className="text-xs text-muted-foreground">
                          v: 1
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
                        {evt.fires}
                      </p>
                      <CodeBlock
                        code={JSON.stringify(evt.examplePayload, null, 2)}
                        language="JSON"
                        testId={`webhooks-event-${evt.name}-payload`}
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
                  Contract events (<code>contract.created</code>,{" "}
                  <code>contract.signed</code>, <code>contract.analyzed</code>,
                  …) and <code>matter.created</code> /{" "}
                  <code>task.assigned</code> are also subscribable but use a
                  freer <code>data</code> shape — they predate the strict v1
                  schema and are kept as-is for back-compatibility.
                </p>
              </section>

              {/* Filters */}
              <section
                id="filters"
                className="scroll-mt-24"
                data-testid="section-filters"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Filter className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {docs.filters.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {docs.filters.body}
                </p>
                <CodeBlock
                  code={docs.filters.code}
                  language="JSON"
                  testId="webhooks-docs-filters-code"
                />
              </section>

              {/* Signing */}
              <section
                id="signing"
                className="scroll-mt-24"
                data-testid="section-signing"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {docs.signing.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {docs.signing.body}
                </p>
                <CodeBlock
                  code={docs.signing.headerFormat}
                  language="Header"
                  testId="webhooks-docs-signing-header"
                />
                <div className="mt-6 rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-5">
                  <h3 className="text-sm font-semibold text-navy dark:text-white mb-2">
                    Secret rotation
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {docs.signing.rotationNote}
                  </p>
                </div>
              </section>

              {/* Verifier snippets */}
              <section
                id="verifiers"
                className="scroll-mt-24"
                data-testid="section-verifiers"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan flex items-center justify-center">
                    <Code2 className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    Verifier snippets
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  Drop these into your receiver to validate every delivery
                  before processing it. Each snippet rejects timestamps older
                  than 5 minutes (the same window the signer uses) and
                  compares the HMAC in constant time.
                </p>
                <div className="space-y-6">
                  {docs.verifiers.map((v) => (
                    <div key={v.lang} data-testid={`webhooks-verifier-${v.lang}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <h3 className="text-h4 text-navy dark:text-white">
                          {v.label}
                        </h3>
                        <code className="text-xs font-mono text-muted-foreground">
                          {v.filename}
                        </code>
                      </div>
                      <CodeBlock
                        code={v.code}
                        language={v.label}
                        testId={`webhooks-verifier-${v.lang}-code`}
                      />
                    </div>
                  ))}
                </div>
              </section>

              {/* Retry */}
              <section
                id="retry"
                className="scroll-mt-24"
                data-testid="section-retry"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                    <Repeat className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {docs.retry.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {docs.retry.body}
                </p>
                <div className="rounded-2xl border border-mist overflow-hidden mb-6">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-navy/40 text-left">
                      <tr>
                        <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Attempt
                        </th>
                        <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Wait before this attempt
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-mist">
                      {docs.retry.schedule.map((row) => (
                        <tr key={row.attempt}>
                          <td className="px-4 py-2.5 font-mono text-slate-700 dark:text-slate-200">
                            {row.attempt}
                          </td>
                          <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200">
                            {row.wait}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-4">
                  <div className="rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-5">
                    <h3 className="text-sm font-semibold text-navy dark:text-white mb-2">
                      Idempotency
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {docs.retry.idempotencyNote}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-5">
                    <h3 className="text-sm font-semibold text-navy dark:text-white mb-2">
                      What counts as success
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {docs.retry.successNote}
                    </p>
                  </div>
                </div>
              </section>

              {/* Footer CTA */}
              <div className="rounded-3xl bg-navy text-white p-10 md:p-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-brand-gradient opacity-10 mix-blend-overlay" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold mb-2">
                      Need a hand wiring it up?
                    </h2>
                    <p className="text-slate-300 max-w-xl text-sm">
                      Customers can pair with our integrations engineers to
                      stand up Jira, NetSuite, ServiceNow, or a custom
                      forwarder.
                    </p>
                  </div>
                  <Link href="/company/contact">
                    <span className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-white px-6 text-sm font-semibold text-navy hover:bg-slate-100 transition-colors cursor-pointer">
                      Contact us
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
